import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Button,
  Input,
  Badge
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import IntlMessages from "Util/IntlMessages";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import messagesData from "Data/messages.json";
import PerfectScrollbar from "react-perfect-scrollbar";

type ThreadMessage = {
  id: string;
  from: "me" | "them";
  text: string;
  time: string;
};

export default function TeamMessageReply() {
  const intl = useIntl();
  const { contactId } = useParams();
  const contact = useMemo(
    () => messagesData.data.find(m => String(m.key) === String(contactId)),
    [contactId]
  );

  const [draft, setDraft] = useState("");
  const [thread, setThread] = useState<ThreadMessage[]>([]);

  useEffect(() => {
    if (!contact) return;
    setThread([
      {
        id: "seed-1",
        from: "them",
        text: `${contact.message} Can we sync on this soon?`,
        time: contact.time
      },
      {
        id: "seed-2",
        from: "me",
        text: "Thanks — I saw your note. I will review and get back to you today.",
        time: "Earlier"
      }
    ]);
  }, [contact]);

  const sendReply = () => {
    const text = draft.trim();
    if (!text) return;
    setThread(prev => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        from: "me",
        text,
        time: "Just now"
      }
    ]);
    setDraft("");
  };

  if (!contact) {
    return (
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="team-message.not-found-title" />}
          />
          <Separator className="mb-4" />
          <Card className="team-message-card">
            <CardBody>
              <p className="text-muted">
                <IntlMessages id="team-message.not-found-body" />
              </p>
              <Button tag={Link} color="primary" to="/app/main/layout/main">
                <IntlMessages id="team-message.back-dashboard" />
              </Button>
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    );
  }

  return (
    <>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="team-message.title" />}
          />
          <p className="text-muted mb-0">
            <IntlMessages id="team-message.subtitle" />
          </p>
          <Separator className="mb-4" />
        </Colxx>
      </Row>

      <Row className="team-message-layout">
        <Colxx xxs="12" lg="4" className="mb-4 mb-lg-0">
          <Card className="team-message-card team-message-profile-card h-100">
            <CardBody>
              <Button
                tag={Link}
                color="link"
                className="ps-0 mb-3 text-small"
                to="/app/main/layout/main"
              >
                ← <IntlMessages id="team-message.back-dashboard" />
              </Button>

              <div className="text-center mb-4">
                <img
                  src={contact.thumb}
                  alt=""
                  className="team-message-profile-card__avatar rounded-circle mb-3"
                />
                <h4 className="mb-1">{contact.label}</h4>
                <Badge
                  color={
                    contact.online === "Online" ? "success" : "secondary"
                  }
                  pill
                  className="text-uppercase fontsize10px"
                >
                  {contact.online}
                </Badge>
                <p className="text-muted text-small mt-3 mb-0">
                  <IntlMessages id="team-message.last-activity" />{" "}
                  <strong>{contact.time}</strong>
                </p>
              </div>

              <Separator className="my-3" />

              <CardTitle className="text-small text-muted text-uppercase mb-3">
                <IntlMessages id="team-message.quick-actions" />
              </CardTitle>
              <div className="d-grid gap-2">
                <Button color="light" outline className="text-start" disabled>
                  <i className="simple-icon-phone me-2" aria-hidden />
                  <IntlMessages id="team-message.placeholder-call" />
                </Button>
                <Button color="light" outline className="text-start" disabled>
                  <i className="simple-icon-camera me-2" aria-hidden />
                  <IntlMessages id="team-message.placeholder-video" />
                </Button>
              </div>
            </CardBody>
          </Card>
        </Colxx>

        <Colxx xxs="12" lg="8">
          <Card className="team-message-card team-message-chat-card h-100">
            <CardBody className="d-flex flex-column p-0">
              <div className="px-4 pt-4 pb-2 border-bottom">
                <CardTitle className="mb-0 text-small text-muted text-uppercase">
                  <IntlMessages id="team-message.thread" />
                </CardTitle>
              </div>

              <div className="team-message-thread flex-grow-1 px-4 py-3">
                <PerfectScrollbar
                  option={{ suppressScrollX: true, wheelPropagation: false }}
                >
                  <div className="pe-2">
                    {thread.map(msg => (
                      <div
                        key={msg.id}
                        className={`team-message-bubble mb-3 ${
                          msg.from === "me"
                            ? "team-message-bubble--me"
                            : "team-message-bubble--them"
                        }`}
                      >
                        <div className="team-message-bubble__meta d-flex flex-wrap align-items-center text-small text-muted mb-1">
                          {msg.from === "me" ? (
                            <IntlMessages id="team-message.you" />
                          ) : (
                            contact.label
                          )}
                          <span className="ms-2">{msg.time}</span>
                        </div>
                        <div className="team-message-bubble__body">
                          {msg.text}
                        </div>
                      </div>
                    ))}
                  </div>
                </PerfectScrollbar>
              </div>

              <div className="team-message-composer border-top px-4 py-3 mt-auto">
                <label className="text-small text-muted text-uppercase d-block mb-2">
                  <IntlMessages id="team-message.reply-label" />
                </label>
                <Input
                  type="textarea"
                  rows={3}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: "team-message.reply-placeholder"
                  })}
                  className="mb-3"
                  onKeyDown={e => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                />
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Button color="primary" type="button" onClick={sendReply}>
                    <IntlMessages id="team-message.send" />
                  </Button>
                  <span className="text-muted text-small">
                    <IntlMessages id="team-message.send-hint" />
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    </>
  );
}
